/**
 * Toast taxonomy — standardized notification helpers.
 *
 * Use these helpers instead of calling `react-hot-toast` directly so we stay
 * consistent on duration, icons, and copy patterns across the app.
 *
 * Categories:
 *   - notify.saved(entity)        → "Customer saved"     (2s success)
 *   - notify.created(entity)      → "Customer created"   (2s success)
 *   - notify.updated(entity)      → "Customer updated"   (2s success)
 *   - notify.deleted(entity)      → "Customer deleted"   (2s success)
 *   - notify.action(verb, what?)  → "Refund issued" / "Refund issued for INV-1023"
 *   - notify.warning(message)     → amber toast (4s)
 *   - notify.error(err, fallback?) → red toast, 5s. Message precedence:
 *       API response.data.message → caller fallback → raw err.message → generic.
 *   - notify.info(message)        → neutral 3s toast
 *   - notify.promise(...)         → wraps an async mutation with loading→success/error
 *
 * For one-off custom toasts, you can still import `toast` from react-hot-toast directly.
 */
import toast, { type ToastOptions } from 'react-hot-toast';

const SUCCESS: ToastOptions = { duration: 2000 };
const INFO: ToastOptions = { duration: 3000 };
const WARNING: ToastOptions = { duration: 4000, icon: '⚠️' };
const ERROR: ToastOptions = { duration: 5000 };

const GENERIC_ERROR = 'Something went wrong';

/**
 * Resolve a user-facing error string. Precedence (T-408):
 *   API message (response.data.message) → caller `fallback` → raw err.message → generic.
 * The caller's tailored/localized fallback wins over the raw axios `err.message`
 * ("Request failed with status code 500"), which only surfaces when the caller
 * supplied no fallback (so no-body network/5xx errors show the page's own copy).
 */
function extractError(err: unknown, fallback?: string): string {
  if (err && typeof err === 'object') {
    const anyErr = err as { response?: { data?: { message?: string | string[] } }; message?: string };
    const apiMsg = anyErr.response?.data?.message;
    if (Array.isArray(apiMsg) && typeof apiMsg[0] === 'string') return apiMsg[0];
    if (typeof apiMsg === 'string') return apiMsg;
    if (fallback) return fallback;
    if (typeof anyErr.message === 'string' && anyErr.message) return anyErr.message;
  }
  return fallback ?? GENERIC_ERROR;
}

/**
 * Public accessor for the resolved error message (same precedence as notify.error),
 * for handlers that need the string directly rather than toasting it (T-407).
 */
export function getApiErrorMessage(err: unknown, fallback?: string): string {
  return extractError(err, fallback);
}

export const notify = {
  saved: (entity: string) => toast.success(`${entity} saved`, SUCCESS),
  created: (entity: string) => toast.success(`${entity} created`, SUCCESS),
  updated: (entity: string) => toast.success(`${entity} updated`, SUCCESS),
  deleted: (entity: string) => toast.success(`${entity} deleted`, SUCCESS),
  action: (verb: string, what?: string) =>
    toast.success(what ? `${verb} for ${what}` : verb, SUCCESS),

  info: (message: string) => toast(message, INFO),
  warning: (message: string) => toast(message, WARNING),

  error: (err: unknown, fallback?: string) =>
    toast.error(extractError(err, fallback), ERROR),

  promise: <T,>(
    promise: Promise<T>,
    messages: { loading: string; success: string; error: string },
  ) => toast.promise(promise, messages),
};

export default notify;
